import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getErrorMessage,
    useMutationWithHandler,
} from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import DownLoadIcons from '@/icons/DownLoadIcons'
import ImageSourcePicker from '@/modules/Invoice/components/SignedDocument/ImageSourcePicker'
import ResignDialog from '@/modules/Invoice/components/SignedDocument/ResignDialog'
import SendMailDialog from '@/modules/Invoice/components/SignedDocument/SendMailDialog'
import SignedDocumentTimeline from '@/modules/Invoice/components/SignedDocument/SignedDocumentTimeline'
import { INVOICE_STATUS_OPTIONS } from '@/modules/Invoice/constants'
import { SIGNED_DOCUMENTS } from '@/modules/Invoice/constants/signed-documents'
import {
    signedDocumentQueryKey,
    useSignedDocument,
} from '@/modules/Invoice/hooks/use-signed-document'
import { useSignedDocumentErrorText } from '@/modules/Invoice/hooks/use-signed-document-error-text'
import { useSignedDocumentImages } from '@/modules/Invoice/hooks/use-signed-document-images'
import {
    getInvoiceByInvoiceId,
    getSignedDocumentPreview,
    signSignedDocument,
} from '@/modules/Invoice/services'
import {
    SignedDocument,
    SignedDocumentImageSource,
    SignedDocumentType,
} from '@/modules/Invoice/types'
import { ModalProps } from '@/types/shared'
import {
    formatActorName,
    formatDocumentTime,
} from '@/modules/Invoice/utils/signed-document'
import { useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Mail, RefreshCw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, Loading } from 'vnetwork-ui'

export interface SignedDocumentModalProps extends ModalProps {
    /** Loại chứng từ: giấy đề nghị thanh toán / biên bản đối soát */
    documentType: SignedDocumentType
    invoiceID: number
    invoiceStatus?: string
}

// Tham số mở PDF (Chrome/Edge: toolbar, navpanes; Firefox: pagemode) — URL fragment nên không ảnh hưởng chữ ký presign
const SIGNED_PDF_VIEW_PARAMS = '#toolbar=0&navpanes=0&pagemode=none&view=FitH'

// Link file đã ký sống 1 giờ; còn dưới mức này thì lấy link mới trước khi mở
const FILE_URL_MIN_REMAINING_MS = 60_000

const STATUS_STYLES: Record<SignedDocument['status'], string> = {
    pending: 'bg-amber-50 text-amber-800 border-amber-200',
    signed: 'bg-green-50 text-green-800 border-green-200',
    failed: 'bg-red-50 text-red-800 border-red-200',
    cancelled: 'bg-gray-50 text-gray-700 border-gray-200',
}

/**
 * Popup chứng từ ký số (giấy đề nghị thanh toán / biên bản đối soát): xem trước, gửi ký,
 * xem / tải PDF đã ký, ký lại, lịch sử; hình ảnh (loại hasImages) và gửi mail (loại hasMail).
 */
const SignedDocumentModal: React.FC<SignedDocumentModalProps> = ({
    documentType,
    invoiceID,
    invoiceStatus,
    open,
    onClose,
}) => {
    const { t } = useTranslation('invoice')
    const config = SIGNED_DOCUMENTS[documentType]
    const documentNames = {
        document: t(`${config.i18nKey}.name`),
        Document: t(`${config.i18nKey}.name-capitalized`),
    }
    const title = t(`${config.i18nKey}.title`)
    // Bản dịch mã lỗi nằm ở locale module (invoice:signed_document_errors), không ở axios-error
    const errorText = useSignedDocumentErrorText(documentType)

    // Trạng thái hoá đơn lấy mới mỗi lần mở popup: `invoiceStatus` truyền vào có thể là bản cache
    // cũ (staleTime mặc định 5 phút) → vừa xác nhận hoá đơn mà popup vẫn khoá nút gửi ký tới khi F5.
    // Dùng chung key với trang chi tiết nên cập nhật luôn nút / badge ở đó. `invoiceStatus` chỉ dùng
    // tạm trong lúc chờ.
    const { data: invoice } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.INVOICE_DETAIL, invoiceID],
            queryFn: () => getInvoiceByInvoiceId(invoiceID),
            staleTime: 0,
        },
        { showLoading: false, showError: false }
    )

    const {
        data: signedDoc,
        refetch,
        isFetching,
        isLoading: isDocumentLoading,
    } = useSignedDocument(documentType, invoiceID)

    const isSigned = signedDoc?.status === 'signed'
    // Bản mới nhất `cancelled` (hiếm: ký lại chưa tạo được bản mới) coi như chưa gửi ký
    const current = signedDoc?.status === 'cancelled' ? null : signedDoc
    const [resignOpen, setResignOpen] = useState(false)
    const [sendMailOpen, setSendMailOpen] = useState(false)

    // Nguồn trang "Hình ảnh" (chỉ loại hasImages) cho xem trước + gửi ký + ký lại. Mặc định `auto`;
    // chứng từ đã có hình upload thì tự chọn `upload` (một lần, user đổi rồi thì giữ lựa chọn).
    const [imageSource, setImageSource] =
        useState<SignedDocumentImageSource>('auto')
    const imageSourceTouched = useRef(false)
    const { images, isFetched: imagesFetched } = useSignedDocumentImages(
        documentType,
        invoiceID,
        !isSigned || resignOpen
    )
    useEffect(() => {
        if (imagesFetched && images.length > 0 && !imageSourceTouched.current) {
            imageSourceTouched.current = true
            setImageSource('upload')
        }
    }, [imagesFetched, images.length])
    const changeImageSource = (source: SignedDocumentImageSource) => {
        imageSourceTouched.current = true
        setImageSource(source)
    }
    // Loại không có trang Hình ảnh: không gửi image_source (service cũng bỏ qua)
    const activeImageSource = config.hasImages ? imageSource : undefined

    // Đã ký thì xem file PDF đã ký. preview.html dựng lại từ dữ liệu hoá đơn
    // hiện tại, không có chữ ký và có thể lệch với bản đã ký.
    const preview = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.INVOICE_SIGNED_DOCUMENT_PREVIEW,
                documentType,
                invoiceID,
                activeImageSource ?? 'none',
            ],
            queryFn: () =>
                getSignedDocumentPreview(
                    documentType,
                    invoiceID,
                    activeImageSource
                ),
            enabled: !isDocumentLoading && !isSigned,
            // Bản xem trước dựng từ dữ liệu hoá đơn hiện tại: lấy mới mỗi lần mở (không dùng
            // cache 5 phút mặc định, hoá đơn có thể vừa được sửa / xác nhận)
            staleTime: 0,
        },
        {
            renderError: (error) => (
                <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                    {errorText(getErrorMessage(error), {
                        fallback: 'preview-failed',
                    })}
                </div>
            ),
        }
    )

    const queryClient = useQueryClient()

    // Ghi response (gửi ký / ký lại / gửi mail) thẳng vào cache, không gọi GET lại.
    // Response gửi ký không kèm history / mails: giữ phần đang có.
    const storeResult = (result: SignedDocument) => {
        queryClient.setQueryData<SignedDocument | null>(
            signedDocumentQueryKey(documentType, invoiceID),
            (previous) => ({
                ...result,
                history: result.history ?? previous?.history,
                mails: result.mails ?? previous?.mails,
                last_sent_at: result.last_sent_at ?? previous?.last_sent_at,
            })
        )
    }

    // Kết quả gửi ký / ký lại: ký phiên thường trả luôn `signed` kèm link file
    const handleSignResult = (result: SignedDocument) => {
        storeResult(result)
        if (result.status === 'failed') {
            toast.error(errorText(result.error_message))
        } else {
            toast.success(
                t(
                    result.status === 'signed'
                        ? 'signed-document-signed-success'
                        : 'signed-document-sign-pending',
                    documentNames
                )
            )
        }
    }

    const { mutateAsync: sign, isPending: isSigning } = useMutationWithHandler(
        {
            mutationFn: () =>
                signSignedDocument(documentType, invoiceID, activeImageSource),
        },
        {
            // Chi tiết (nhãn nút, badge) và danh sách (cột trạng thái) đọc <type>_status
            reactQueryKeys: QUERY_KEYS.INVOICES,
            onSuccess: (response) => {
                if (response?.result) handleSignResult(response.result)
            },
            // useMutationWithHandler chỉ dịch qua axios-error/translation, không thấy
            // namespace invoice: tự toast bằng errorText.
            showErrorToast: false,
            onError: (error) => {
                toast.error(errorText(getErrorMessage(error)))
                // Lỗi (vd hết phiên ký) vẫn có thể đã ghi lần gửi thất bại: đọc lại trạng thái
                refetch()
            },
        }
    )

    // Trạng thái có thể đổi trong lúc mở popup (polling): đóng popup thì làm mới chi tiết + danh sách
    const openedStatus = useRef<SignedDocument['status'] | null>()
    if (openedStatus.current === undefined && !isDocumentLoading) {
        openedStatus.current = signedDoc?.status ?? null
    }
    const handleClose = () => {
        if (
            openedStatus.current !== undefined &&
            openedStatus.current !== (signedDoc?.status ?? null)
        ) {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INVOICES] })
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.INVOICE_DETAIL, invoiceID],
            })
        }
        onClose()
    }

    const getFreshSignedFile = async () => {
        const expiresAt = signedDoc?.file_url_expires_at
            ? new Date(signedDoc.file_url_expires_at).getTime()
            : 0
        if (
            signedDoc?.file_url &&
            expiresAt - Date.now() >= FILE_URL_MIN_REMAINING_MS
        ) {
            return signedDoc
        }
        const { data } = await refetch()
        return data
    }

    const handleOpenSignedFile = async () => {
        const file = await getFreshSignedFile()
        if (file?.file_url)
            window.open(file.file_url, '_blank', 'noopener,noreferrer')
    }

    const handleDownloadSignedFile = async () => {
        const file = await getFreshSignedFile()
        // file_download_url trả Content-Disposition: attachment nên trang không bị điều hướng.
        // API chưa có field này thì mở tab mới, tải từ viewer PDF.
        if (file?.file_download_url)
            window.location.assign(file.file_download_url)
        else if (file?.file_url)
            window.open(file.file_url, '_blank', 'noopener,noreferrer')
    }

    const isApproved =
        (invoice?.result?.invoice_status ?? invoiceStatus) ===
        INVOICE_STATUS_OPTIONS.Approved
    const canSign =
        isApproved &&
        !!preview.data &&
        (!current || current.status === 'failed')

    const statusBanner = current && (
        <div
            className={`mb-3 rounded-md border px-4 py-2 text-sm ${STATUS_STYLES[current.status]}`}
        >
            <div>
                <span className="font-medium">
                    {t(`signed-document-status-${current.status}`)}
                </span>
                {current.status === 'failed' && current.error_message && (
                    <span> — {errorText(current.error_message)}</span>
                )}
                {isSigned && config.hasMail && current.last_sent_at && (
                    <span>
                        {' '}
                        ·{' '}
                        {t('signed-document-mail-last-sent', {
                            time: formatDocumentTime(current.last_sent_at),
                        })}
                    </span>
                )}
            </div>
            {/* Ai gửi ký, khi nào (+ ký xong lúc nào); giờ VN dd/MM/yyyy HH:mm */}
            <div className="mt-0.5 text-xs opacity-80">
                {t('signed-document-requested-by', {
                    name: formatActorName(current.requested_by_name),
                    time: formatDocumentTime(
                        current.requested_at ?? current.created_at
                    ),
                })}
                {isSigned &&
                    current.signed_at &&
                    ` · ${t('signed-document-signed-done-at', {
                        time: formatDocumentTime(current.signed_at),
                    })}`}
            </div>
        </div>
    )

    const actions = (
        <div className="flex w-full flex-wrap items-center justify-end gap-2">
            {isSigning && (
                <span className="mr-auto text-sm text-gray-500">
                    {t('signed-document-signing-hint')}
                </span>
            )}
            {!isApproved && !isSigned && !isSigning && (
                <span className="mr-auto text-sm text-gray-500">
                    {t('signed_document_errors.invoice-not-approved')}
                </span>
            )}
            <Button variant="outlined" onClick={handleClose}>
                {t('close')}
            </Button>
            {signedDoc?.status === 'pending' && (
                <Button
                    variant="outlined"
                    onClick={() => refetch()}
                    loading={isFetching}
                    disabled={isFetching}
                >
                    {t('refresh')}
                </Button>
            )}
            {isSigned ? (
                <>
                    {/* Ký lại: huỷ bản đã ký + ký bản mới. Quyền invoices:approved do backend chặn
                        (giống nút gửi ký / xác nhận hoá đơn, FE chỉ check trạng thái hoá đơn) */}
                    {isApproved && (
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<RefreshCw className="h-4 w-4" />}
                            onClick={() => setResignOpen(true)}
                        >
                            {t('signed-document-sign-again')}
                        </Button>
                    )}
                    {/* Gửi bản đã ký cho khách (loại hasMail); quyền invoices:approved do backend chặn */}
                    {config.hasMail && (
                        <Button
                            variant="outlined"
                            startIcon={<Mail className="h-4 w-4" />}
                            onClick={() => setSendMailOpen(true)}
                        >
                            {t(
                                current?.last_sent_at
                                    ? 'signed-document-mail-resend'
                                    : 'signed-document-mail-button'
                            )}
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        startIcon={<ExternalLink className="h-4 w-4" />}
                        onClick={handleOpenSignedFile}
                    >
                        {t('signed-document-open-new-tab')}
                    </Button>
                    <Button
                        startIcon={<DownLoadIcons />}
                        onClick={handleDownloadSignedFile}
                    >
                        {t('signed-document-download-signed')}
                    </Button>
                </>
            ) : (
                <Button
                    onClick={() => sign()}
                    loading={isSigning}
                    disabled={!canSign || isSigning}
                >
                    {t(
                        current?.status === 'failed'
                            ? 'signed-document-resign'
                            : 'signed-document-sign'
                    )}
                </Button>
            )}
        </div>
    )

    const renderContent = () => {
        if (isDocumentLoading) return <Loading.FadingDots />

        if (isSigned) {
            return signedDoc?.file_url ? (
                // PDF hiện bằng viewer của trình duyệt (không sandbox: Chrome chặn PDF trong iframe sandbox).
                // Ẩn thanh công cụ + cột thumbnail, chỉ còn trang PDF; mở/tải đã có nút riêng ở footer.
                <iframe
                    title={title}
                    src={`${signedDoc.file_url}${SIGNED_PDF_VIEW_PARAMS}`}
                    className="h-[70vh] w-full border-0 bg-white"
                />
            ) : (
                <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                    {t('signed-document-signed-file-missing')}
                </div>
            )
        }

        return (
            <>
                {/* Chọn nguồn hình trước khi xem trước / ký: bản đã ký không sửa được, chỉ Ký lại */}
                {config.hasImages && (
                    <ImageSourcePicker
                        type={documentType}
                        invoiceID={invoiceID}
                        value={imageSource}
                        onChange={changeImageSource}
                        disabled={isSigning}
                    />
                )}
                {preview.renderState() ?? (
                    <div className="overflow-x-auto">
                        {/* iframe để CSS của chứng từ (khổ A4) và CSS của app không đè lên nhau */}
                        <iframe
                            title={title}
                            srcDoc={preview.data}
                            sandbox=""
                            className="h-[70vh] w-full min-w-[820px] border-0 bg-white"
                        />
                    </div>
                )}
            </>
        )
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            title={title}
            actions={actions}
        >
            {statusBanner}
            {signedDoc && (
                // Timeline gộp: gửi ký / đã ký / huỷ để ký lại (mọi bản) + mail gửi khách
                <SignedDocumentTimeline
                    documentType={documentType}
                    latest={signedDoc}
                    mails={config.hasMail ? signedDoc.mails : undefined}
                />
            )}
            {renderContent()}
            {resignOpen && (
                <ResignDialog
                    open
                    documentType={documentType}
                    invoiceID={invoiceID}
                    onClose={() => setResignOpen(false)}
                    imageSource={imageSource}
                    onImageSourceChange={changeImageSource}
                    onResigned={handleSignResult}
                    onFailed={() => refetch()}
                />
            )}
            {config.hasMail && sendMailOpen && (
                <SendMailDialog
                    open
                    documentType={documentType}
                    invoiceID={invoiceID}
                    onClose={() => setSendMailOpen(false)}
                    onSent={storeResult}
                    onFailed={() => refetch()}
                />
            )}
        </Dialog>
    )
}

export default SignedDocumentModal
